/* Keep every available fabric visible, with optional search. Selection and pricing stay with the studios. */
(() => {
  'use strict';
  if (window.KaliaFabricPicker) return;
  const normalize = value => String(value || '').trim().toLocaleLowerCase();
  class KaliaFabricPicker {
    constructor(studio) {
      this.studio = studio;
      this.groups = new Map();
      this.queue = this.queue.bind(this);
      this.observer = new MutationObserver(this.queue);
      this.observer.observe(studio,{childList:true,subtree:true,attributes:true,attributeFilter:['hidden','disabled','checked']});
      for (const name of ['change','kalia:product-studio-ready','kalia:product-configuration-change','focusin','focusout']) studio.addEventListener(name,this.queue);
      this.scan();
    }
    queue() {
      if (this.queued) return;
      this.queued = true;
      queueMicrotask(() => { this.queued = false; this.scan(); });
    }
    node(tag, className) {
      const node = document.createElement(tag);
      node.className = className;
      return node;
    }
    scan() {
      this.studio.querySelectorAll('.kps-swatch-grid, #kd-field-color .kd-swatch-list').forEach(grid => {
        const choices = [...grid.querySelectorAll('.kps-swatch, .kd-swatch')];
        const first = choices[0]?.querySelector('input[type="radio"]');
        if (!first || !choices.length) return;
        const key = first.name;
        let group = this.groups.get(key);
        if (!group) { group = {key,query:''}; this.groups.set(key,group); }
        const fieldset = grid.closest('fieldset') || grid.parentElement;
        let search = fieldset.querySelector('.kps-swatch-search, [data-fabric-search]');
        if (!search && choices.length > 12 && this.studio.matches('kalia-drapery')) {
          search = this.node('input','kfp-search'); search.type = 'search';
          search.dataset.fabricSearch = ''; search.placeholder = 'Search color or code';
          search.setAttribute('aria-label','Search colors'); search.autocomplete = 'off';
          grid.parentNode.insertBefore(search,grid);
        }
        if (search !== group.search) {
          const previous = group.search;
          group.search = search;
          if (search) {
            // Retain an edited search across a late studio rebuild, while accepting any newer typed value.
            if (previous && !search.value && group.query) search.value = group.query;
            group.query = search.value || '';
            search.addEventListener('input',() => {
              if (group.search !== search) return;
              group.query = search.value;
              this.queue();
            });
          }
        }
        if (group.grid !== grid || !group.bar?.isConnected) {
          group.bar?.remove();
          group.grid = grid;
          const bar = this.node('div','kfp-actions');
          const result = this.node('span','kfp-result'); result.setAttribute('role','status'); result.setAttribute('aria-live','polite');
          const clear = this.node('button','kfp-clear'); clear.type = 'button'; clear.textContent = 'Clear search'; clear.hidden = true;
          clear.addEventListener('click',() => {
            const search = group.search;
            if (group.clear !== clear || !clear.isConnected || !search?.isConnected || !this.studio.contains(search)) return;
            search.value = '';
            // The shade studio owns native hidden/availability. Re-run its existing
            // search listener rather than exposing labels or changing any radios.
            search.dispatchEvent(new Event('input',{bubbles:true}));
            search.focus({preventScroll:true});
          });
          bar.append(result,clear); grid.after(bar);
          group.bar = bar; group.result = result; group.clear = clear;
        }
        group.choices = choices;
        grid.dataset.fabricPicker = '';
        this.render(group);
      });
    }
    render(group) {
      const query = normalize(group.search?.value ?? group.query);
      group.query = group.search?.value ?? group.query;
      const items = group.choices.map(label => ({label,input:label.querySelector('input[type="radio"]'),frame:label.closest('.kiz-wrap') || label}));
      const available = items.filter(item => item.input && !item.input.disabled && !item.label.hidden);
      const matches = available.filter(item => !query || normalize(item.label.title || item.input.getAttribute('aria-label') || item.input.value).includes(query));
      const focused = items.find(item => item.frame.contains(document.activeElement));
      const visible = new Set(matches);
      if (focused && !focused.label.hidden) visible.add(focused);
      items.forEach(item => {
        // Core availability remains authoritative; never write disabled, checked, value or native hidden.
        const hidden = !visible.has(item) && !item.input?.disabled;
        item.label.dataset.fabricChoice = '';
        item.label.dataset.fabricFiltered = String(hidden);
        item.label.dataset.fabricSelected = String(Boolean(item.input?.checked));
      });
      const resultText = matches.length ? `${matches.length} ${matches.length === 1 ? 'color' : 'colors'}` : 'No matching colors';
      if (group.result.textContent !== resultText) group.result.textContent = resultText;
      const hideClear = !group.search?.value;
      if (group.clear.hidden !== hideClear) group.clear.hidden = hideClear;
      const hideBar = !group.search && !query;
      if (group.bar.hidden !== hideBar) group.bar.hidden = hideBar;
      group.grid.dataset.fabricExpanded = 'true';
    }
  }
  window.KaliaFabricPicker = KaliaFabricPicker;
  const attached = new WeakSet();
  const start = () => document.querySelectorAll('kalia-product-studio, kalia-drapery').forEach(studio => {
    if (attached.has(studio)) return;
    attached.add(studio); new KaliaFabricPicker(studio);
  });
  document.addEventListener('shopify:section:load',start);
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded',start,{once:true}); else start();
})();
